import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import moment from 'moment'
import type { Moment } from 'moment'
import { axiosInstance } from '@/modules/shared/lib/axios'
import type { BonusConfigurationModel } from '@/app/models/timesheet/bonusConfigurationModel'
import type { TimesheetModel } from '@/app/models/timesheetModel'
import type { OperationResultModel } from '@/app/models/application/operationResultModel'
import type { ScheduleModel } from '@/app/models/timesheet/scheduleModel'
import type { ScheduleDayModel } from '@/app/models/timesheet/scheduleDayModel'
import type { DriverLeaveSummaryModel } from '@/app/models/timesheet/leaveSummaryModel'
import { SERVICE_DATE_FORMAT } from '@/app/constants/date'
import { getContractType } from '@/app/services/contract/contractTypeResolver'
import { getSettlementType } from '@/app/services/contract/settlementTypeResolver'
import { getActivitySource } from '@/app/services/activity/activitySourceResolver'
import { getDayType } from '@/app/services/calendar/dayTypeResolver'

const useTimesheetService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const getGroupedTimesheets = async ({
    companyId,
    driversIds,
    year,
    month,
    showBonusConfiguration = false,
  }: {
    companyId: string
    driversIds: string[]
    year: number
    month: number
    showBonusConfiguration?: boolean
  }) => {
    const stopLoading = startLoading()
    try {
      // Convert driver IDs from strings to numbers (API expects numbers)
      const driverNumbers = driversIds
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id))

      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/groupedTimesheet`,
        {
          companyId,
          drivers: driverNumbers,
          year,
          month,
          showBonusConfiguration,
        },
      )
      return res.data?.data || res.data || []
    } catch (err) {
      console.error('Error fetching grouped timesheets:', err)
      return []
    } finally {
      stopLoading()
    }
  }

  const getDriverTimesheet = async ({
    companyId,
    driverId,
    date,
    showLoading = false,
  }: {
    companyId: string
    driverId: string | number
    date: string
    showLoading?: boolean
  }) => {
    const stopLoading = showLoading ? startLoading() : () => {}
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/driverTimesheet`,
        {
          driverId:
            typeof driverId === 'string' ? parseInt(driverId, 10) : driverId,
          date,
        },
      )
      return res.data?.data || res.data || {}
    } catch (err) {
      console.error('Error fetching driver timesheet:', err)
      return {}
    } finally {
      stopLoading()
    }
  }

  const calculateTimesheet = async ({
    companyId,
    drivers,
    startTime,
    endTime,
  }: {
    companyId: string
    drivers: number[]
    startTime: string
    endTime: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/calculate`,
        {
          drivers,
          startTime,
          endTime,
        },
      )
      return res.data?.data || res.data || {}
    } catch (err) {
      console.error('Error calculating timesheet:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const calculateTimesheetLegacy = async ({
    companyId,
    drivers,
    months,
    year,
    outOfDateQuarterExchangeRateAccepted = false,
    bonusConfigurations,
    id,
  }: {
    companyId: string
    drivers: number[]
    months: number[]
    year: number
    outOfDateQuarterExchangeRateAccepted?: boolean
    bonusConfigurations: Array<BonusConfigurationModel>
    id: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/calculate`,
        {
          companyId,
          drivers,
          months,
          year,
          outOfDateQuarterExchangeRateAccepted,
          bonusConfigurations,
          id,
        },
      )
      return res.data?.data || res.data || {}
    } catch (err) {
      console.error('Error calculating timesheet:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const importRewardFile = async ({
    companyId,
    file,
  }: {
    companyId: string
    file: File
  }) => {
    const stopLoading = startLoading()
    try {
      const formData = new FormData()
      formData.append('body', file)
      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/fileUpload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return res.data?.data || res.data || {}
    } catch (err) {
      console.error('Error importing reward file:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const precalculateTimesheet = async ({
    companyId,
    drivers,
    months,
    year,
    outOfDateQuarterExchangeRateAccepted = false,
    id,
  }: {
    companyId: string
    drivers: number[]
    months: number[]
    year: number
    outOfDateQuarterExchangeRateAccepted?: boolean
    id: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/precalculate`,
        {
          companyId,
          drivers,
          months,
          year,
          outOfDateQuarterExchangeRateAccepted,
          id,
        },
      )
      return res.data?.data || res.data || {}
    } catch (err) {
      console.error('Error precalculating timesheet:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const getTimesheet = async ({
    companyId,
    driverId,
    date,
  }: {
    companyId: string
    driverId: string | number
    date: Moment | string
  }): Promise<TimesheetModel | null> => {
    const stopLoading = startLoading()
    try {
      const dateStr =
        typeof date === 'string'
          ? date
          : moment(date).format(SERVICE_DATE_FORMAT)

      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/driverTimesheet`,
        {
          driverId:
            typeof driverId === 'string' ? parseInt(driverId, 10) : driverId,
          date: dateStr,
        },
      )

      const data = res.data?.data || res.data

      if (!data) {
        return null
      }

      // Map the response to TimesheetModel format
      const timesheet: TimesheetModel = {
        rows:
          data.rows?.map((r: any) => ({
            title: r.title,
            titleKey: r.titleKey,
            isChildEvent: r.isChildEvent,
            isWorkTimeEvent: r.isWorkTimeEvent,
            appliedRuleKeys: r.appliedRuleKeys || [],
            cells:
              r.cells?.map((c: any) => ({
                date: moment(c.date),
                value: c.value,
                isHoliday: c.isHoliday,
                isScheduledWorkingDay: c.isScheduledWorkingDay,
                dayType: getDayType(c.dayType),
                toolTip: c.toolTip ?? null,
                hasWarning: !!c.hasWarning,
              })) || [],
          })) || [],
        dayActivities:
          data.dayActivities?.map((a: any) => ({
            activityBlocks: a?.activityBlocks,
            activityDayLocalTimeDate: a.activityDayLocalTimeDate,
            dayNumber: a.dayNumber,
            cardAcitivities:
              a.cardAcitivities?.map((ca: any) => getActivitySource(ca)) || [],
            isHoliday: a.isHoliday,
            restAllDay: a.restAllDay,
            originDayType: a.originDayType,
            tripData: a.tripData,
          })) || [],
        calculationExists: data.calculationExists || false,
        workTime: data.workTime,
        applicationVersion: data.applicationVersion || '',
        type: getContractType(data.type),
        settlement: getSettlementType(data.settlement),
        calculationDate: data.calculationDate
          ? moment(data.calculationDate)
          : null,
        calculationUser: data.calculationUser || '',
        calculationUserEmail: data.calculationUserEmail || null,
        appliedSettings: data.appliedSettings || null,
      }

      return timesheet
    } catch (err) {
      console.error('Error fetching timesheet:', err)
      return null
    } finally {
      stopLoading()
    }
  }

  const changeTimesheetDayTypes = async ({
    companyId,
    driverId,
    dates,
    dayType,
    notes,
  }: {
    companyId: string
    driverId: string | number
    dates: Array<Moment | string>
    dayType: number
    notes: string
  }): Promise<Array<OperationResultModel>> => {
    const stopLoading = startLoading()
    try {
      const datesStr = dates.map((d) =>
        typeof d === 'string' ? d : moment(d).format(SERVICE_DATE_FORMAT),
      )

      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/changeTimesheetDayType`,
        {
          driverId:
            typeof driverId === 'string' ? parseInt(driverId, 10) : driverId,
          dayType,
          dates: datesStr,
          notes,
        },
      )

      return res.data?.data || res.data || []
    } catch (err) {
      console.error('Error changing timesheet day types:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const getSchedule = async ({
    companyId,
    driverId,
    year,
  }: {
    companyId: string
    driverId: string | number
    year: number
  }): Promise<ScheduleModel | null> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/timesheet/schedule?&driverId=${driverId}&year=${year}`,
      )

      const data = res.data?.content?.data || res.data?.data || res.data

      if (!data) {
        return null
      }

      const mapScheduleFields = (scheduleData: any): ScheduleDayModel => {
        if (scheduleData == null) {
          return null
        }

        return {
          dayType: getDayType(scheduleData.dayType),
          dayNumber: scheduleData.dayNumber,
          dayDate: moment(scheduleData.dayDate),
          dayTypeSymbol: scheduleData.dayTypeSymbol,
          dayStartTime: scheduleData.dayStartTime
            ? moment(scheduleData.dayStartTime)
            : null,
          dayScheduleTime: scheduleData.dayScheduleTime,
          showSchedule: scheduleData.showSchedule ?? false,
          isHoliday: scheduleData.isHoliday,
        }
      }

      const scheduleModel: ScheduleModel = {
        days: [],
        hoursInMonth: data.hoursInMonth || [],
        periods: data.periods || [],
        hasData: data.hasData || false,
        annualLeave: data.annualLeave || 0,
        leaves: data.leaves || 0,
        notPaidLeaves: data.notPaidLeaves || 0,
        onRequestLeaves: data.onRequestLeaves || 0,
        overhours: data.overhours || null,
        overhoursLimit: data.overhoursLimit || null,
        sickLeaves: data.sickLeaves || 0,
      }

      if (data.days && Array.isArray(data.days)) {
        for (const row of data.days) {
          const daysInMonth: Array<ScheduleDayModel> = row.map((c) =>
            mapScheduleFields(c),
          )
          scheduleModel.days.push(daysInMonth)
        }
      }

      return scheduleModel
    } catch (err) {
      console.error('Error fetching schedule:', err)
      return null
    } finally {
      stopLoading()
    }
  }


  const getDriverLeaveSummary = async ({
    companyId,
    driverId,
    year,
  }: {
    companyId: string
    driverId: string | number
    year: number
  }): Promise<DriverLeaveSummaryModel | null> => {
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/timesheet/leaveSummary?driverId=${driverId}&year=${year}`,
      )

      const data = res.data?.content?.data || res.data?.data || res.data
      if (!data || typeof data !== 'object') return null
      if (data.hasContract === false) return null
      if (data.annualLeaveDays == null && data.usedLeaveDays == null) return null

      return {
        hasContract: data.hasContract ?? true,
        annualLeaveDays: Number(data.annualLeaveDays) || 0,
        overdueLeaveDays: Number(data.overdueLeaveDays) || 0,
        usedLeaveDays: Number(data.usedLeaveDays) || 0,
        usedOnDemandLeaveDays: Number(data.usedOnDemandLeaveDays) || 0,
        usedOverdueLeaveDays:
          data.usedOverdueLeaveDays != null
            ? Number(data.usedOverdueLeaveDays) || 0
            : undefined,
        onDemandLeaveLimit: Number(data.onDemandLeaveLimit) || 0,
        remainingLeaveDays: Number(data.remainingLeaveDays) || 0,
      }
    } catch (err) {
      return null
    }
  }

  const removeTimesheet = async ({
    companyId,
    driverId,
    date,
  }: {
    companyId: string
    driverId: string | number
    date: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/removeTimesheet`,
        {
          driverId:
            typeof driverId === 'string' ? parseInt(driverId, 10) : driverId,
          date,
        },
      )
      return res.data?.data || res.data || {}
    } catch (err) {
      console.error('Error removing timesheet:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const changeDayTypes = async ({
    companyId,
    driverId,
    dates,
    dayType,
    notes,
    applyScheduleHours = false,
  }: {
    companyId: string
    driverId: number
    dates: string[]
    dayType: number
    notes: string
    applyScheduleHours?: boolean
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/driverCalendar/changeDayTypes`,
        { driverId, notes, dayType, dates, applyScheduleHours },
      )
      return res.data
    } finally {
      stopLoading()
    }
  }
  const previewCalculateTimesheet = async ({
    companyId,
    drivers,
    months,
    year,
    outOfDateQuarterExchangeRateAccepted = false,
    bonusConfigurations,
    id,
  }: {
    companyId: string
    drivers: number[]
    months: number[]
    year: number
    outOfDateQuarterExchangeRateAccepted?: boolean
    bonusConfigurations: Array<BonusConfigurationModel>
    id: string
  }): Promise<TimesheetModel | null> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/preview-calculate`,
        {
          companyId,
          drivers,
          months,
          year,
          outOfDateQuarterExchangeRateAccepted,
          bonusConfigurations,
          id,
        },
      )
      const data = res.data?.data || res.data
      if (!data) return null

      const timesheet: TimesheetModel = {
        rows:
          data.rows?.map((r: any) => ({
            title: r.title,
            titleKey: r.titleKey,
            isChildEvent: r.isChildEvent,
            isWorkTimeEvent: r.isWorkTimeEvent,
            appliedRuleKeys: r.appliedRuleKeys || [],
            cells:
              r.cells?.map((c: any) => ({
                date: moment(c.date),
                value: c.value,
                isHoliday: c.isHoliday,
                isScheduledWorkingDay: c.isScheduledWorkingDay,
                dayType: getDayType(c.dayType),
                toolTip: c.toolTip ?? null,
                hasWarning: !!c.hasWarning,
              })) || [],
          })) || [],
        dayActivities:
          data.dayActivities?.map((a: any) => ({
            activityDayLocalTimeDate: a.activityDayLocalTimeDate,
            dayNumber: a.dayNumber,
            cardAcitivities:
              a.cardAcitivities?.map((ca: any) => getActivitySource(ca)) || [],
            isHoliday: a.isHoliday,
            restAllDay: a.restAllDay,
            originDayType: a.originDayType,
            tripData: a.tripData,
          })) || [],
        calculationExists: true,
        workTime: data.workTime,
        applicationVersion: data.applicationVersion || '',
        type: getContractType(data.type),
        settlement: getSettlementType(data.settlement),
        calculationDate: null,
        calculationUser: '',
        calculationUserEmail: null,
        appliedSettings: data.appliedSettings || null,
      }

      return timesheet
    } catch (err) {
      console.error('Error preview calculating timesheet:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  return {
    changeDayTypes,
    getGroupedTimesheets,
    getDriverTimesheet,
    getTimesheet,
    calculateTimesheet,
    calculateTimesheetLegacy,
    previewCalculateTimesheet,
    importRewardFile,
    precalculateTimesheet,
    changeTimesheetDayTypes,
    getSchedule,
    getDriverLeaveSummary,
    removeTimesheet,
  }
}

export default useTimesheetService
